import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  AccountBalance as DepositIcon,
  SwapHoriz as TransferIcon,
  MoneyOff as RefundIcon,
  Remove as DeductIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { PageHeader, Loading, EmptyState } from '../components/Layout';
import api from '../services/api';
import tenantService from '../services/tenantService';

const Deposits = () => {
  const [deposits, setDeposits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('add'); // add, transfer, refund, deduct
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [tenantInvoices, setTenantInvoices] = useState([]);
  const [formData, setFormData] = useState({
    tenant_id: '',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    notes: '',
    transfer_amount: '',
    invoice_id: '',
    deduction_amount: '',
    deduction_reason: '',
  });

  useEffect(() => {
    fetchDeposits();
    fetchTenants();
  }, []);

  const fetchDeposits = async () => {
    try {
      setLoading(true);
      console.log('Fetching deposits from API...');
      const response = await api.get('/api/deposits');
      console.log('Deposits API response:', response);
      console.log('Deposits data:', response.data);
      console.log('Deposits count:', Array.isArray(response.data) ? response.data.length : 0);
      
      if (Array.isArray(response.data)) {
        setDeposits(response.data);
        if (response.data.length > 0) {
          console.log('Sample deposit:', response.data[0]);
        }
      } else {
        console.warn('Unexpected response format - not an array:', response.data);
        setDeposits([]);
      }
    } catch (error) {
      console.error('Failed to load deposits:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      if (error.response?.status !== 404) {
        toast.error('Failed to load deposits');
      }
      setDeposits([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await tenantService.getAllTenants();
      setTenants(response.data.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to load tenants:', error);
    }
  };

  const fetchTenantInvoices = async (tenantId) => {
    try {
      const response = await api.get(`/api/invoices?tenant_id=${tenantId}`);
      // Filter for unpaid or partially paid invoices
      const unpaidInvoices = response.data.filter(
        inv => inv.status === 'pending' || inv.status === 'partial_paid' || inv.status === 'overdue'
      );
      setTenantInvoices(unpaidInvoices);
    } catch (error) {
      console.error('Failed to load tenant invoices:', error);
      setTenantInvoices([]);
    }
  };

  const handleOpenDialog = async (type, deposit = null) => {
    setDialogType(type);
    setSelectedDeposit(deposit);
    if (deposit) {
      setFormData({
        ...formData,
        tenant_id: deposit.tenant_id,
        amount: deposit.amount,
        invoice_id: '',
      });
      // Fetch tenant's unpaid invoices if transferring
      if (type === 'transfer') {
        await fetchTenantInvoices(deposit.tenant_id);
      }
    } else {
      setFormData({
        tenant_id: '',
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
        notes: '',
        transfer_amount: '',
        invoice_id: '',
        deduction_amount: '',
        deduction_reason: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDeposit(null);
  };

  const handleDeleteDeposit = async (deposit) => {
    const confirmed = window.confirm(
      `Delete deposit of KES ${deposit.amount?.toLocaleString()} for ${deposit.tenant_name}?\n\nThis action cannot be undone.`
    );
    if (!confirmed) return;
    try {
      await api.delete(`/api/deposits/${deposit.id}`);
      toast.success('Deposit deleted successfully');
      fetchDeposits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete deposit');
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    try {
      switch (dialogType) {
        case 'add':
          await api.post('/api/deposits', {
            tenant_id: formData.tenant_id,
            amount: parseFloat(formData.amount),
            payment_date: formData.payment_date,
            payment_method: formData.payment_method,
            notes: formData.notes,
          });
          toast.success('Deposit recorded successfully');
          break;
        case 'transfer':
          if (!formData.invoice_id) {
            toast.error('Please select an invoice');
            return;
          }
          await api.post(`/api/deposits/${selectedDeposit.id}/transfer-to-rent`, {
            invoice_id: parseInt(formData.invoice_id),
            amount: parseFloat(formData.transfer_amount),
          });
          toast.success('Deposit transferred to rent successfully');
          break;
        case 'refund':
          await api.post(`/api/deposits/${selectedDeposit.id}/refund`, {
            amount: parseFloat(formData.amount),
            notes: formData.notes,
          });
          toast.success('Deposit refunded successfully');
          break;
        case 'deduct':
          await api.post(`/api/deposits/${selectedDeposit.id}/deduct`, {
            amount: parseFloat(formData.deduction_amount),
            reason: formData.deduction_reason,
          });
          toast.success('Deduction applied successfully');
          break;
        default:
          break;
      }
      handleCloseDialog();
      fetchDeposits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
      console.error(error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'held':
        return 'primary';
      case 'refunded':
        return 'success';
      case 'partially_used':
        return 'warning';
      case 'fully_used':
        return 'default';
      default:
        return 'default';
    }
  };

  const filteredDeposits = deposits.filter(d => {
    if (tabValue === 0) return true; // All
    if (tabValue === 1) return d.status === 'held';
    if (tabValue === 2) return d.status === 'partially_used';
    if (tabValue === 3) return d.status === 'refunded';
    return true;
  });

  const totalDeposits = deposits.reduce((sum, d) => sum + (d.amount || 0), 0);
  const heldDeposits = deposits.filter(d => d.status === 'held').reduce((sum, d) => sum + (d.remaining_amount || d.amount || 0), 0);
  const refundedDeposits = deposits.filter(d => d.status === 'refunded').reduce((sum, d) => sum + (d.amount || 0), 0);

  if (loading) {
    return <Loading message="Loading deposits..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Deposits Management"
        subtitle="Track and manage tenant security deposits"
        breadcrumbs={[{ label: 'Deposits' }]}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('add')}
          >
            Record Deposit
          </Button>
        }
      />

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Total Deposits
                  </Typography>
                  <Typography variant="h4">KES {totalDeposits.toLocaleString()}</Typography>
                </Box>
                <DepositIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Held Deposits
                  </Typography>
                  <Typography variant="h4">KES {heldDeposits.toLocaleString()}</Typography>
                </Box>
                <DepositIcon sx={{ fontSize: 48, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Refunded
                  </Typography>
                  <Typography variant="h4">KES {refundedDeposits.toLocaleString()}</Typography>
                </Box>
                <RefundIcon sx={{ fontSize: 48, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label={`All (${deposits.length})`} />
          <Tab label={`Held (${deposits.filter(d => d.status === 'held').length})`} />
          <Tab label={`Partially Used (${deposits.filter(d => d.status === 'partially_used').length})`} />
          <Tab label={`Refunded (${deposits.filter(d => d.status === 'refunded').length})`} />
        </Tabs>
      </Paper>

      {/* Deposits Table */}
      {filteredDeposits.length === 0 ? (
        <EmptyState
          title="No deposits yet"
          description="Start by recording security deposits for your tenants."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('add')}
            >
              Record First Deposit
            </Button>
          }
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Tenant</strong></TableCell>
                <TableCell><strong>Room</strong></TableCell>
                <TableCell><strong>Amount (KES)</strong></TableCell>
                <TableCell><strong>Remaining (KES)</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDeposits.map((deposit) => (
                <TableRow key={deposit.id}>
                  <TableCell>{deposit.tenant_name || 'N/A'}</TableCell>
                  <TableCell>{deposit.room_number || 'N/A'}</TableCell>
                  <TableCell>{deposit.amount?.toLocaleString()}</TableCell>
                  <TableCell>{(deposit.remaining_amount || deposit.amount)?.toLocaleString()}</TableCell>
                  <TableCell>
                    {new Date(deposit.payment_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={deposit.status?.toUpperCase().replace('_', ' ')}
                      color={getStatusColor(deposit.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog('transfer', deposit)}
                      title="Transfer to Rent"
                      disabled={deposit.status === 'refunded' || deposit.status === 'fully_used'}
                    >
                      <TransferIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => handleOpenDialog('refund', deposit)}
                      title="Refund Deposit"
                      disabled={deposit.status === 'refunded'}
                    >
                      <RefundIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="warning"
                      onClick={() => handleOpenDialog('deduct', deposit)}
                      title="Apply Deduction"
                      disabled={deposit.status === 'refunded' || deposit.status === 'fully_used'}
                    >
                      <DeductIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteDeposit(deposit)}
                      title="Delete Deposit"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog for Add/Transfer/Refund/Deduct */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogType === 'add' && 'Record Security Deposit'}
          {dialogType === 'transfer' && 'Transfer Deposit to Rent'}
          {dialogType === 'refund' && 'Refund Security Deposit'}
          {dialogType === 'deduct' && 'Apply Deduction'}
        </DialogTitle>
        <DialogContent>
          {dialogType === 'add' && (
            <>
              <TextField
                fullWidth
                select
                label="Tenant"
                name="tenant_id"
                value={formData.tenant_id}
                onChange={handleInputChange}
                margin="normal"
                required
              >
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.full_name} - {tenant.email}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Deposit Amount (KES)"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleInputChange}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Payment Date"
                name="payment_date"
                type="date"
                value={formData.payment_date}
                onChange={handleInputChange}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                fullWidth
                select
                label="Payment Method"
                name="payment_method"
                value={formData.payment_method}
                onChange={handleInputChange}
                margin="normal"
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="mpesa">M-Pesa</MenuItem>
                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                <MenuItem value="card">Card</MenuItem>
              </TextField>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                margin="normal"
                multiline
                rows={2}
              />
            </>
          )}
          {dialogType === 'transfer' && (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Available: KES {(selectedDeposit?.remaining_amount || selectedDeposit?.amount || 0).toLocaleString()}
              </Typography>
              <TextField
                fullWidth
                select
                label="Select Invoice"
                name="invoice_id"
                value={formData.invoice_id}
                onChange={handleInputChange}
                margin="normal"
                required
                helperText={tenantInvoices.length === 0 ? 'No unpaid invoices found for this tenant' : 'Select the invoice to apply deposit to'}
              >
                {tenantInvoices.map((invoice) => (
                  <MenuItem key={invoice.id} value={invoice.id}>
                    {invoice.invoice_number} - KES {invoice.amount?.toLocaleString()} ({invoice.status})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Amount to Transfer (KES)"
                name="transfer_amount"
                type="number"
                value={formData.transfer_amount}
                onChange={handleInputChange}
                margin="normal"
                required
                inputProps={{ max: selectedDeposit?.remaining_amount || selectedDeposit?.amount }}
              />
            </>
          )}
          {dialogType === 'refund' && (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Available: KES {(selectedDeposit?.remaining_amount || selectedDeposit?.amount || 0).toLocaleString()}
              </Typography>
              <TextField
                fullWidth
                label="Refund Amount (KES)"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleInputChange}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Refund Notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                margin="normal"
                multiline
                rows={2}
                placeholder="No damages found, full refund"
              />
            </>
          )}
          {dialogType === 'deduct' && (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Available: KES {(selectedDeposit?.remaining_amount || selectedDeposit?.amount || 0).toLocaleString()}
              </Typography>
              <TextField
                fullWidth
                label="Deduction Amount (KES)"
                name="deduction_amount"
                type="number"
                value={formData.deduction_amount}
                onChange={handleInputChange}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Deduction Reason"
                name="deduction_reason"
                value={formData.deduction_reason}
                onChange={handleInputChange}
                margin="normal"
                multiline
                rows={3}
                required
                placeholder="Broken window, damaged furniture, unpaid bills..."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogType === 'add' && 'Record'}
            {dialogType === 'transfer' && 'Transfer'}
            {dialogType === 'refund' && 'Refund'}
            {dialogType === 'deduct' && 'Apply Deduction'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Deposits;
